import React, { useEffect, useRef, useState } from 'react';
// ui
import { Drawer } from '@mui/material';
import { NotificationManager } from 'react-notifications';
import { useDispatch, useSelector } from 'react-redux';

import addToListIcon from '../../../images/discovery/demographics/addToList.svg';
// images
import backIcon from '../../../images/discovery/demographics/backIcon.svg';
import savePdfIcon from '../../../images/discovery/demographics/savePdf.svg';
import poweredBySaralImage from '../../../images/icons/logos/poweredBySaral.svg';
import { fetchUserInfo } from '../../../redux/user/userActions';
// utils
import { getInfluencerDemographics, getInfluencerDemographicsData } from '../../../services/discovery.service';
import { calculateTimeDifferenceInMinutes } from '../../../utils/global';
import IconContainer from '../../common-components/FtuxComponent/IconContainer';
import OutlinedButton from '../../styledComponents/OutlinedButton.style';
import PrimaryButton from '../../styledComponents/PrimaryButton.style';
import AddToList from './demographicsComponents/AddToList';
import AgeAndGenderCard from './demographicsComponents/AgeAndGenderCard';
import AudienceLocationCard from './demographicsComponents/AudienceLocationCard';
import AudienceLocationCityCard from './demographicsComponents/AudienceLocationCityCard';
import AudienceTopInterests from './demographicsComponents/AudienceTopInterests';
import AudienceType from './demographicsComponents/AudienceType';
import BrandAffinityCard from './demographicsComponents/BrandAffinityCard';
import EngagementRateCard from './demographicsComponents/EngagementRateCard';
import FollowersGraph from './demographicsComponents/FollowersGraph';
import LoadingComponent from './demographicsComponents/LoadingComponent';
import OtherSponsoredPosts from './demographicsComponents/OtherSponsoredPosts';
import PopularPosts from './demographicsComponents/PopularPosts';
import PopularReels from './demographicsComponents/PopularReels';
// components
import ProfileCard from './demographicsComponents/ProfileCard';
import ProfileStatsCards from './demographicsComponents/ProfileStatsCards';
import SimilarInfluencers from './demographicsComponents/SimilarInfluencers';
import TopHashtagsCard from './demographicsComponents/TopHashtagsCard';
import TopMentionsCard from './demographicsComponents/TopMentionsCard';
// styles
import './demographics.scss';
import '../styles/_relationship.scss';
import './styles/posts.scss';

import { getIsUserOnLitePlan } from '@/utils/auth-utils';
import { _calculateDieHardFans } from '@/utils/ts/stringUtils';

export default function DemographicsDrawer({
  open,
  onClose,
  selectedInfluencer,
  demographics_id,
  revealDemographics = true,
  influencerId,
  fetchProfile,
  setShowOutofRatnaModal,
  setShowLitePlanModal = () => {},
  showAddToListButton = true,
  profile,
}) {
  const [demographicsData, setDemographicsData] = useState(undefined);
  const [demographicsStatus, setDemographicsStatus] = useState('loading');
  const [demographicsCreationDate, setDemographicsDate] = useState('');
  const [demographicsId, setDemographicsId] = useState('');
  const [numberOfCalls, setNumberOfCalls] = useState(0);
  const [showAddToList, setShowAddToList] = useState('');
  const [showWaitingMessage, setShowWaitingMessage] = useState(true);
  const demographicsRef = useRef();
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);

  const handleCloseDrawer = () => {
    onClose();
    setNumberOfCalls(0);
    setDemographicsStatus('loading');
  };

  const fetchDemographicsData = async () => {
    const body = { ...selectedInfluencer };
    if (influencerId) {
      body.influencerId = influencerId;
    }
    const response = await getInfluencerDemographicsData(body);
    if (response?.status) {
      const status = response.data.demographics.status;
      setDemographicsStatus(status);
      setDemographicsData(response.data.demographics.audienceData);
      setDemographicsId(response.data.demographics._id);
      setDemographicsDate(response.data.demographics.createdAt);
      if (status !== 'pending') setNumberOfCalls(7);
      dispatch(fetchUserInfo());
    } else {
      if (response?.message === 'You have insufficient balance to perform this action.') {
        if (getIsUserOnLitePlan(userDetails)) {
          setShowLitePlanModal('ratna');
        } else {
          setShowOutofRatnaModal(true);
        }
        handleCloseDrawer();
      } else {
        NotificationManager.error(response.message || 'Error fetching demographics');
      }
    }
  };

  const fetchDemographicsDataById = async () => {
    const response = await getInfluencerDemographics(demographics_id);
    if (response?.status) {
      const status = response.data.status;
      setDemographicsStatus(status);
      setDemographicsData(response.data.audienceData);
      setDemographicsId(response.data._id);
    }
  };

  const printDemographics = () => {
    document.title = `${demographicsData.user_profile.fullname
      .toLowerCase()
      .replaceAll(' ', '-')}-SARAL-demographics-${userDetails?.first_name.toLowerCase()}`;
    var printContents = demographicsRef.current.innerHTML;
    var originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    document.body.style.padding = '25px';

    const logoContainer = document.getElementById('logo-container');
    logoContainer.style.display = 'block';

    window.print();
    window.close();

    document.title = 'SARAL - Your Influencer Assistant';
    document.body.innerHTML = originalContents;
    logoContainer.style.display = 'none';
    document.body.style.padding = '0';
    window.location.reload();
  };

  useEffect(() => {
    if (revealDemographics && open && numberOfCalls < 7) {
      const interval = setInterval(() => {
        fetchDemographicsData();
        setNumberOfCalls((prev) => prev + 1);
      }, 2000);
      return () => clearInterval(interval);
    } else if (!revealDemographics && demographics_id && open) {
      fetchDemographicsDataById();
      setShowWaitingMessage(false);
    } else if (!revealDemographics && open) {
      fetchDemographicsDataById();
      setShowWaitingMessage(false);
    }
    if (influencerId && numberOfCalls === 7) {
      fetchProfile(false);
      setShowWaitingMessage(false);
    }
  }, [numberOfCalls, open, revealDemographics.influencerId]);

  return (
    <Drawer anchor="right" open={open} onClose={handleCloseDrawer}>
      <div className="drawer-main-container">
        <div style={{ display: 'flex' }}>
          <button className="demographics-back-btn" onClick={handleCloseDrawer}>
            <img src={backIcon} alt="back icon" />
          </button>
          {showWaitingMessage && (demographicsStatus === 'loading' || demographicsStatus === 'pending') && (
            <div className="demographics-loading-message">
              Fetching real-time data. Check notifications on the top right after 2 minutes. You don’t have to wait
              here!
            </div>
          )}
        </div>

        {/* Initally the status would be loading until the first API response is received */}
        {demographicsStatus === 'loading' && (
          <div className="demographics-loader-wrapper">
            <LoadingComponent />
          </div>
        )}

        {/* After first response, if status is pending and 10 mins have not passed after the first API request then continue to show loader*/}
        {demographicsStatus === 'pending' &&
          (calculateTimeDifferenceInMinutes(new Date(), new Date(demographicsCreationDate)) <= 10 ||
            isNaN(calculateTimeDifferenceInMinutes(new Date(), new Date(demographicsCreationDate)))) && (
            <div className="demographics-loader-wrapper">
              <LoadingComponent />
            </div>
          )}

        {/* if status is pending and 10 mins have passed for the first API call then the data for that particular creater is not available */}
        {demographicsStatus === 'pending' &&
          calculateTimeDifferenceInMinutes(new Date(), new Date(demographicsCreationDate)) > 10 && (
            <div className="demographics-loader-wrapper">
              <IconContainer icon="📊" />
              <p>Demographics data is not available for this creator!</p>
            </div>
          )}

        {/* if status of demographics is notfound then show this message */}
        {demographicsStatus === 'notfound' && (
          <div className="demographics-loader-wrapper">
            <IconContainer icon="📊" />
            <p>Demographics for this creator are not available at the moment.</p>
          </div>
        )}

        {/* if status of demographics is retrylater then show this message */}
        {demographicsStatus === 'retrylater' && (
          <div className="demographics-loader-wrapper">
            <IconContainer icon="📊" />
            <p>
              The demographics are being fetched in real-time. Don&apos;t wait here. We&apos;ll send you a notification
              (top right 🔔) when they&apos;re available. Give it 2-3 minutes!
            </p>
          </div>
        )}

        {/* if status of demographics is success then show the data */}
        {demographicsStatus === 'success' && demographicsData && (
          <>
            <div id="demographics" ref={demographicsRef}>
              <div className="demographics-row">
                <ProfileCard userDetails={demographicsData.user_profile} profile={profile} />
                <ProfileStatsCards
                  userDetails={demographicsData.user_profile}
                  // SAR-978 new calculation for die_hard_fans
                  audienceCredebility={_calculateDieHardFans(
                    demographicsData?.user_profile?.avg_comments,
                    demographicsData?.user_profile?.avg_likes
                  )}
                  aov_factor={userDetails?.aov_factor}
                  profile={profile}
                />
              </div>
              <h1 className="demographics-title" style={{ marginBottom: '-10px' }}>
                Statistics
              </h1>
              <div className="demographics-row">
                <FollowersGraph statsHistory={demographicsData.user_profile.stat_history} />
                <EngagementRateCard
                  engrateData={demographicsData.extra.engagement_rate_histogram}
                  engagementRate={demographicsData.user_profile?.engagement_rate || profile?.engagement_rate}
                  profileImage={demographicsData.user_profile?.picture}
                  username={
                    demographicsData.user_profile?.username ||
                    demographicsData.user_profile?.fullname?.replaceAll(' ', '').toLowerCase()
                  }
                />
              </div>
              <div className="demographics-row">
                <TopHashtagsCard tags={demographicsData.user_profile.top_hashtags} />
                <TopMentionsCard mentions={demographicsData.user_profile.top_mentions} />
              </div>
              <div className="demographics-row">
                <BrandAffinityCard brandAffinityData={demographicsData.user_profile.brand_affinity} />
                <SimilarInfluencers similarInfluencersData={demographicsData.user_profile.similar_users} />
              </div>
              <div className="demographics-row">
                <AudienceType audienceTypeData={demographicsData?.audience_followers?.data?.audience_types} />
                <AudienceTopInterests
                  audienceTopInterestsData={demographicsData?.audience_followers?.data?.audience_interests}
                />
              </div>
              <div className="demographics-row">
                <AgeAndGenderCard
                  ageGenderData={demographicsData?.audience_followers?.data?.audience_genders_per_age}
                />
              </div>
              {demographicsData?.audience_followers?.data?.audience_geo?.countries && (
                <div className="demographics-row">
                  <AudienceLocationCard
                    locationData={demographicsData?.audience_followers.data?.audience_geo.countries?.slice(0, 10)}
                  />
                </div>
              )}
              {demographicsData?.audience_followers?.data?.audience_geo?.cities && (
                <div className="demographics-row">
                  <AudienceLocationCityCard
                    locationData={demographicsData?.audience_followers.data?.audience_geo.cities}
                  />
                </div>
              )}
              {(demographicsData?.user_profile?.commercial_posts ||
                demographicsData?.user_profile?.commercial_posts?.length > 0) && (
                <div className="demographics-row">
                  <OtherSponsoredPosts sponseredPostsData={demographicsData.user_profile.commercial_posts} />
                </div>
              )}
              {(demographicsData.user_profile.top_posts || demographicsData.user_profile.top_posts?.length > 0) && (
                <div className="demographics-row">
                  <PopularPosts popularPostsData={demographicsData.user_profile.top_posts} />
                </div>
              )}
              {(demographicsData.user_profile.top_reels || demographicsData.user_profile.top_reels?.length > 0) && (
                <div className="demographics-row">
                  <PopularReels popularReelsData={demographicsData.user_profile.top_reels} />
                </div>
              )}
              <div id="logo-container">
                <img src={poweredBySaralImage} alt="" width={200} />
              </div>
            </div>
            <div className="bottom-buttons-container">
              {showAddToListButton && (
                <PrimaryButton onClick={(e) => setShowAddToList(e.currentTarget)}>
                  <img src={addToListIcon} alt="add to list" height={22} />
                  Add to List
                </PrimaryButton>
              )}
              <OutlinedButton style={{ background: '#fff' }} onClick={printDemographics}>
                <img src={savePdfIcon} alt="save pdf" height={22} />
                Download as PDF
              </OutlinedButton>
            </div>
          </>
        )}

        {showAddToList && (
          <AddToList
            anchorEl={showAddToList}
            setAnchorEl={setShowAddToList}
            selectedInfluencer={selectedInfluencer}
            demographicsId={demographicsId}
          />
        )}
      </div>
    </Drawer>
  );
}
